import { Card, EmptyState, Skeleton } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import type { components } from '../../api/schema';
import { ErreurChargement, Section } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { formatEuros, formatNombre } from '../../lib/format';
import { LIBELLE_TYPE } from '../../lib/labels';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';

import { ColonnesJour, type Categorie, type Jour } from './ColonnesJour';

type Details = components['schemas']['AnalyticsDetails'];
type Coach = Details['coaches'][number];
type Module = Details['modules'][number];
type Cohorte = Details['cohorts'][number];
type JourApi = Details['series'][number];

/** Palette des graphiques (validée : séparation daltonisme et contraste ; le jaune a la vue tableau en relais). */
const BLEU = 'var(--viz-1)';
const ORANGE = 'var(--viz-2)';
const JAUNE = 'var(--viz-3)';

interface Mesure {
  id: string;
  libelle: string;
  categories: Categorie[];
  valeurs: (j: JourApi) => number[];
  format?: (n: number) => string;
}

/** Inscriptions empilées Invitée (jaune) · Normal (bleu) · Immersion (orange) : le jaune ne touche jamais l'orange. */
export const MESURES: Mesure[] = [
  {
    id: 'inscriptions',
    libelle: 'Inscriptions',
    categories: [
      { libelle: LIBELLE_TYPE.GUEST, couleur: JAUNE },
      { libelle: LIBELLE_TYPE.NORMAL, couleur: BLEU },
      { libelle: LIBELLE_TYPE.CHALLENGE3, couleur: ORANGE },
    ],
    valeurs: (j) => [j.signups.GUEST, j.signups.NORMAL, j.signups.CHALLENGE3],
  },
  { id: 'actives', libelle: 'Élèves actives', categories: [{ libelle: 'Élèves actives', couleur: BLEU }], valeurs: (j) => [j.activeStudents] },
  { id: 'messages', libelle: 'Messages', categories: [{ libelle: 'Messages envoyés', couleur: BLEU }], valeurs: (j) => [j.messagesSent] },
  {
    id: 'generations',
    libelle: 'Générations',
    categories: [
      { libelle: 'Images', couleur: BLEU },
      { libelle: 'Vidéos', couleur: ORANGE },
    ],
    valeurs: (j) => [j.imagesGenerated, j.videosGenerated],
  },
  { id: 'credits', libelle: 'Crédits consommés', categories: [{ libelle: 'Crédits consommés', couleur: BLEU }], valeurs: (j) => [j.creditsConsumed] },
  {
    id: 'ventes',
    libelle: 'Ventes',
    categories: [{ libelle: 'Montant encaissé', couleur: BLEU }],
    valeurs: (j) => [j.revenueCents],
    format: (c) => formatEuros(c),
  },
];

function pourcent(p: number | null | undefined): string {
  return p === null || p === undefined ? '—' : `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(p)} %`;
}

/**
 * Analytics (cahier des charges §7.10) : l'usage par jour, les coachs,
 * modules et cohortes les plus actifs, et quelques taux pour piloter l'offre.
 */
export function AnalyticsPage() {
  const { valeurs: f, modifier } = useFiltresUrl({ periode: '30', mesure: 'inscriptions', vue: 'graphique' });
  const donnees = useQuery({
    queryKey: ['analytics', f.periode],
    queryFn: () => lire(api.GET('/api/admin/analytics/details', { params: { query: { days: Number(f.periode) as 7 | 30 | 90 } } })),
    placeholderData: keepPreviousData,
  });
  const d = donnees.data;
  const mesure = MESURES.find((m) => m.id === f.mesure) ?? MESURES[0]!;
  const jours: Jour[] = (d?.series ?? []).map((j) => ({ date: j.date, valeurs: mesure.valeurs(j) }));

  return (
    <>
      <PageHeader
        title="Analytics"
        lead="L’usage de la plateforme par jour (heure de Paris), hors comptes admin."
        actions={
          <div className="segmented" role="group" aria-label="Période">
            {['7', '30', '90'].map((p) => (
              <button key={p} type="button" aria-pressed={f.periode === p} onClick={() => modifier({ periode: p })}>
                {p} jours
              </button>
            ))}
          </div>
        }
      />

      {donnees.isError ? (
        <ErreurChargement erreur={donnees.error} onReessayer={() => void donnees.refetch()} />
      ) : (
        <>
          <div className="kpis kpis--six" aria-busy={donnees.isFetching}>
            <Tuile libelle="Inscriptions" valeur={d && formatNombre(d.totals.signups.NORMAL + d.totals.signups.GUEST + d.totals.signups.CHALLENGE3)}>
              {d && `${formatNombre(d.totals.signups.NORMAL)} Normal · ${formatNombre(d.totals.signups.GUEST)} Invité · ${formatNombre(d.totals.signups.CHALLENGE3)} Immersion`}
            </Tuile>
            <Tuile libelle="Élèves actives" valeur={d && formatNombre(d.totals.activeStudents)}>
              Au moins un message sur la période
            </Tuile>
            <Tuile libelle="Messages" valeur={d && formatNombre(d.totals.messagesSent)} />
            <Tuile libelle="Générations" valeur={d && formatNombre(d.totals.imagesGenerated + d.totals.videosGenerated)}>
              {d && `${formatNombre(d.totals.imagesGenerated)} images · ${formatNombre(d.totals.videosGenerated)} vidéos`}
            </Tuile>
            <Tuile libelle="Crédits consommés" valeur={d && formatNombre(d.totals.creditsConsumed)} />
            <Tuile libelle="Ventes" valeur={d && formatEuros(d.totals.revenueCents)}>
              {d && `${formatNombre(d.totals.sales)} paiement${d.totals.sales > 1 ? 's' : ''}, remboursements déduits`}
            </Tuile>
          </div>

          <Section
            title="Par jour"
            actions={
              <div className="segmented" role="group" aria-label="Affichage">
                <button type="button" aria-pressed={f.vue === 'graphique'} onClick={() => modifier({ vue: 'graphique' })}>
                  Graphique
                </button>
                <button type="button" aria-pressed={f.vue === 'tableau'} onClick={() => modifier({ vue: 'tableau' })}>
                  Tableau
                </button>
              </div>
            }
          >
            <div className="segmented segmented--large" role="group" aria-label="Mesure" style={{ marginBottom: 10 }}>
              {MESURES.map((m) => (
                <button key={m.id} type="button" aria-pressed={mesure.id === m.id} onClick={() => modifier({ mesure: m.id })}>
                  {m.libelle}
                </button>
              ))}
            </div>
            <Card>
              {!d ? (
                <Skeleton height={200} />
              ) : f.vue === 'tableau' ? (
                <TableauJours jours={jours} mesure={mesure} />
              ) : (
                <ColonnesJour jours={jours} categories={mesure.categories} libelle={`${mesure.libelle} par jour`} format={mesure.format} />
              )}
            </Card>
          </Section>

          <Section title="Taux">
            <div className="kpis kpis--compact">
              <Tuile libelle="Actives sur 7 jours" valeur={d && pourcent(d.rates.active7d.percent)}>
                {d && `${formatNombre(d.rates.active7d.count)} sur ${formatNombre(d.rates.students)} élèves`}
              </Tuile>
              <Tuile libelle="Actives sur 30 jours" valeur={d && pourcent(d.rates.active30d.percent)}>
                {d && `${formatNombre(d.rates.active30d.count)} sur ${formatNombre(d.rates.students)} élèves`}
              </Tuile>
              <Tuile libelle="Élèves à 0 crédit" valeur={d && pourcent(d.rates.zeroBalance.percent)}>
                {d && (
                  <Link className="link" to="/eleves?solde=zero">
                    {formatNombre(d.rates.zeroBalance.count)} élèves
                  </Link>
                )}
              </Tuile>
              <Tuile libelle="Invitées passées en Normal" valeur={d && pourcent(d.rates.guestToNormal.percent)}>
                {d && `${formatNombre(d.rates.guestToNormal.moved)} sur ${formatNombre(d.rates.guestToNormal.total)} inscrites Invité`}
              </Tuile>
              <Tuile libelle="Immersion passées en Normal" valeur={d && pourcent(d.rates.challenge3ToNormal.percent)}>
                {d && `${formatNombre(d.rates.challenge3ToNormal.moved)} sur ${formatNombre(d.rates.challenge3ToNormal.total)} inscrites Immersion`}
              </Tuile>
            </div>
            <p className="note">Élèves actives : au moins un message envoyé. Taux calculés sur toutes les élèves, hors admins et comptes supprimés.</p>
          </Section>

          <div className="dash-grid dash-grid--egal">
            <Section title="Coachs les plus utilisés">
              <Classement<Coach>
                libelle="Coachs les plus utilisés"
                lignes={d?.coaches}
                cle={(c) => c.id}
                chargement={!d}
                colonnes={[
                  {
                    id: 'coach',
                    header: 'Coach',
                    cell: (c) => (
                      <div>
                        <div className="cell-main">{c.name}</div>
                        <div className="cell-sub">{c.module}</div>
                      </div>
                    ),
                  },
                  { id: 'messages', header: 'Messages', align: 'right', cell: (c) => <span className="num">{formatNombre(c.messages)}</span> },
                  { id: 'eleves', header: 'Élèves', align: 'right', cell: (c) => <span className="num">{formatNombre(c.students)}</span> },
                ]}
              />
            </Section>
            <Section title="Modules">
              <Classement<Module>
                libelle="Modules"
                lignes={d?.modules}
                cle={(m) => m.id}
                chargement={!d}
                colonnes={[
                  { id: 'module', header: 'Module', cell: (m) => <span className="cell-main">{m.name}</span> },
                  { id: 'messages', header: 'Messages', align: 'right', cell: (m) => <span className="num">{formatNombre(m.messages)}</span> },
                  { id: 'eleves', header: 'Élèves', align: 'right', cell: (m) => <span className="num">{formatNombre(m.students)}</span> },
                ]}
              />
            </Section>
          </div>

          <Section title="Cohortes (description de la clé d’accès)">
            <Classement<Cohorte>
              libelle="Cohortes"
              lignes={d?.cohorts}
              cle={(c) => `${c.keyType}-${c.name ?? ''}`}
              chargement={!d}
              colonnes={[
                {
                  id: 'cohorte',
                  header: 'Cohorte',
                  cell: (c) =>
                    c.name ? (
                      <Link className="link" to={`/cles-acces?search=${encodeURIComponent(c.name)}`}>
                        {c.name}
                      </Link>
                    ) : (
                      <span className="muted">Sans description</span>
                    ),
                },
                { id: 'type', header: 'Type', cell: (c) => LIBELLE_TYPE[c.keyType] },
                { id: 'inscrites', header: 'Inscrites', align: 'right', cell: (c) => <span className="num">{formatNombre(c.registered)}</span> },
                {
                  id: 'actives',
                  header: 'Actives sur la période',
                  align: 'right',
                  cell: (c) => (
                    <span className="num">
                      {formatNombre(c.activeInPeriod)} <span className="cell-sub">({pourcent(c.registered ? Math.round((c.activeInPeriod / c.registered) * 1000) / 10 : null)})</span>
                    </span>
                  ),
                },
                {
                  id: 'normal',
                  header: 'Passées en Normal',
                  align: 'right',
                  cell: (c) => (c.keyType === 'NORMAL' ? <span className="muted">—</span> : <span className="num">{formatNombre(c.movedToNormal)}</span>),
                },
                { id: 'cles', header: 'Clés', align: 'right', secondaire: true, cell: (c) => <span className="num">{formatNombre(c.keys)}</span> },
              ]}
            />
          </Section>
        </>
      )}
    </>
  );
}

function Tuile({ libelle, valeur, children }: { libelle: string; valeur: string | undefined; children?: React.ReactNode }) {
  return (
    <Card className="kpi">
      <div className="kpi__label">{libelle}</div>
      <div className="kpi__value">{valeur ?? <Skeleton width={80} height={28} />}</div>
      {children && <div className="kpi__hint">{children}</div>}
    </Card>
  );
}

function Classement<T>({
  libelle,
  lignes,
  cle,
  colonnes,
  chargement,
}: {
  libelle: string;
  lignes: T[] | undefined;
  cle: (l: T) => string;
  colonnes: Colonne<T>[];
  chargement: boolean;
}) {
  return (
    <DataTable
      libelle={libelle}
      colonnes={colonnes}
      lignes={lignes}
      cleLigne={cle}
      chargement={chargement}
      vide={<EmptyState icon={<BarChart3 size={24} aria-hidden="true" />} title="Aucune activité sur la période" />}
    />
  );
}

/** Vue tableau de la mesure choisie (relais du graphique, accessible et exportable à l'œil). */
function TableauJours({ jours, mesure }: { jours: Jour[]; mesure: Mesure }) {
  const format = mesure.format ?? formatNombre;
  return (
    <div className="table-wrap table-wrap--scroll" style={{ maxHeight: 360, overflowY: 'auto' }}>
      <table className="table" aria-label={`${mesure.libelle} par jour`}>
        <thead>
          <tr>
            <th>Jour</th>
            {mesure.categories.map((c) => (
              <th key={c.libelle} style={{ textAlign: 'right' }}>
                {c.libelle}
              </th>
            ))}
            {mesure.categories.length > 1 && <th style={{ textAlign: 'right' }}>Total</th>}
          </tr>
        </thead>
        <tbody>
          {[...jours].reverse().map((j) => (
            <tr key={j.date}>
              <td>{j.date.split('-').reverse().join('/')}</td>
              {j.valeurs.map((v, k) => (
                <td key={k} style={{ textAlign: 'right' }} className="num">
                  {format(v)}
                </td>
              ))}
              {mesure.categories.length > 1 && (
                <td style={{ textAlign: 'right' }} className="num">
                  {format(j.valeurs.reduce((a, b) => a + b, 0))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
